/**
 * Error Capture Service
 *
 * Story 15.12: AI-Powered Feedback Collection System
 *
 * Перехватывает JS ошибки, unhandled promise rejections и HTTP ошибки.
 * Собирает контекст (URL, стек, последние network запросы) и предлагает
 * пользователю отправить отчёт через Feedback Widget.
 */
import { Injectable, inject, ErrorHandler, NgZone, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { CapturedErrorData, NetworkRequestInfo } from '../../shared/models/feedback.model';
import { AuthService } from './auth.service';

// Максимум запомненных network requests
const MAX_NETWORK_REQUESTS = 10;

// Ошибки, которые игнорируем (шум)
const IGNORED_ERRORS = [
  'ResizeObserver loop',
  'Script error.',
  'Non-Error exception captured',
  'Loading chunk',
];

/**
 * Убирает query string из URL перед тем как он попадёт в отправляемые данные
 * инцидента (Story 99.25 review r1 HIGH-6). Backend маскирует секреты только в
 * diagnostic_data (mask_diagnostic_data) — title/error_data уходят как есть, а
 * query string может содержать PII/токены/секреты (email в параметре поиска,
 * session token, пароль в query при редиректе и т.п.). Путь без query строки
 * достаточен для диагностики "какой endpoint упал/какая страница" — секреты
 * остаются на клиенте и никогда не попадают в БД инцидентов.
 */
function stripQueryString(url: string | undefined | null): string {
  if (!url) return '';
  return url.split('?')[0];
}

@Injectable({ providedIn: 'root' })
export class ErrorCaptureService {
  // LAZY INJECTION: НЕ инжектим AuthService в field напрямую, чтобы избежать circular dependency
  // AuthService → Store → StoreDevtools → ErrorHandler → ErrorCaptureService → AuthService
  // Injector не участвует в цепочке зависимостей, поэтому safe
  private ngZone = inject(NgZone);
  private injector = inject(Injector);

  // Последние network requests для контекста
  private recentRequests: NetworkRequestInfo[] = [];

  // Subject для уведомления Feedback Widget о новой ошибке
  readonly errorCaptured$ = new Subject<CapturedErrorData>();

  // Флаг инициализации
  private initialized = false;

  /**
   * Инициализация глобальных обработчиков ошибок.
   * Вызывается один раз при старте приложения.
   */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Глобальный обработчик JS ошибок
    window.addEventListener('error', (event: ErrorEvent) => {
      this.ngZone.run(() => {
        this.handleError(event.message, event.error?.stack);
      });
    });

    // Unhandled Promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      this.ngZone.run(() => {
        const message = event.reason?.message || String(event.reason);
        const stack = event.reason?.stack;
        this.handleError(message, stack);
      });
    });
  }

  /**
   * Записать сетевой запрос (вызывается из HTTP interceptor).
   *
   * Story 99.25 (review r1 HIGH-6): url санитизируется (query string убирается)
   * ДО сохранения — эти записи попадают в error_data инцидента как есть.
   */
  logNetworkRequest(info: NetworkRequestInfo): void {
    this.recentRequests.push({ ...info, url: stripQueryString(info.url) });
    if (this.recentRequests.length > MAX_NETWORK_REQUESTS) {
      this.recentRequests.shift();
    }
  }

  /**
   * Обработать HTTP ошибку (вызывается из interceptor).
   */
  handleHttpError(error: HttpErrorResponse): void {
    // Игнорируем 401/403 — это ожидаемое поведение auth
    if (error.status === 401 || error.status === 403) return;

    // Story 99.25 (AC-2): status===0 в Angular HttpClient означает, что запрос
    // НЕ получил ответа сервера — отменён навигацией/закрытием вкладки, обрыв
    // сети на стороне клиента, CORS/preflight-сбой или timeout. Это НЕ сигнал
    // о серверной проблеме (прод-журнал подтвердил: сервер отвечал 200 на те же
    // URL — backlog-77/83/86). Стратегия: молчать полностью на status===0 —
    // реальные серверные сбои приходят как 5xx и ловятся ниже; разовые клиентские
    // обрывы не стоят создания ложных P1-инцидентов.
    if (error.status === 0) return;

    // 5xx — серверные сбои
    // 400 Bad Request — failed-операция (напр. сохранение ключа МП), достойна репорта
    // 404 и 422 не ловим: "не найдено" и валидация формы — ожидаемые user-ошибки
    if (error.status >= 500 || error.status === 400) {
      // HIGH-6: stripQueryString — URL мог нести PII/секреты в query (см. выше).
      const message = `HTTP ${error.status}: ${error.statusText} - ${stripQueryString(error.url)}`;
      this.handleError(message);
    }
  }

  /**
   * Сделать скриншот текущей страницы (html2canvas).
   * Возвращает base64 PNG или null при ошибке.
   */
  async captureScreenshot(): Promise<string | null> {
    // html2canvas runs synchronously on the main thread — Promise.race timeout
    // CANNOT interrupt it (event loop blocked → setTimeout never fires).
    // Guard against complex pages that would block for minutes (Story 87.8).
    const MAX_DOM_ELEMENTS = 3000;
    const domCount = document.querySelectorAll('*').length;
    if (domCount > MAX_DOM_ELEMENTS) {
      console.warn(`[ErrorCapture] Page too complex (${domCount} elements > ${MAX_DOM_ELEMENTS}), skipping screenshot`);
      return null;
    }

    try {
      const html2canvas = (await import('html2canvas')).default;
      const target = (document.querySelector('main') as HTMLElement) ?? document.body;
      const canvas = await html2canvas(target, {
        logging: false,
        useCORS: true,
        scale: 0.3,
        height: window.innerHeight,
        windowHeight: window.innerHeight,
        ignoreElements: (el: Element) => el.classList?.contains('feedback-widget'),
      });
      return canvas.toDataURL('image/jpeg', 0.5);
    } catch (err) {
      console.warn('[ErrorCapture] Screenshot capture failed:', err);
      return null;
    }
  }

  /**
   * Получить последние network requests.
   */
  getRecentRequests(): NetworkRequestInfo[] {
    return [...this.recentRequests];
  }

  /**
   * Основной обработчик ошибок.
   */
  private handleError(message: string, stack?: string): void {
    // Фильтруем шум
    if (IGNORED_ERRORS.some(ignored => message.includes(ignored))) {
      return;
    }

    // LAZY RESOLUTION: используем Injector.get() вместо inject()
    // inject() вне injection context вызывает NG0203
    // Injector.get() безопасен в любом контексте (в т.ч. из RxJS callbacks)
    const authService = this.injector.get(AuthService, null);
    const user = authService?.currentUser();

    const errorData: CapturedErrorData = {
      message,
      stack,
      // HIGH-6: pageUrl тоже мог нести query-параметры с PII (поисковые запросы,
      // токены сброса пароля в URL при редиректе и т.п.) — санитизируем.
      pageUrl: stripQueryString(window.location.href),
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      networkRequests: this.getRecentRequests().slice(-3),
      userContext: {
        cabinetId: user?.cabinetId,
        userId: user?.id,
      },
    };

    this.errorCaptured$.next(errorData);
  }
}

/**
 * Global Angular ErrorHandler — интеграция с ErrorCaptureService.
 * Регистрируется как провайдер в app.config.ts.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private errorCapture = inject(ErrorCaptureService);

  handleError(error: unknown): void {
    // Логируем в консоль (стандартное поведение)
    console.error('Unhandled error:', error);

    if (error instanceof HttpErrorResponse) {
      this.errorCapture.handleHttpError(error);
    } else if (error instanceof Error) {
      (this.errorCapture as any)['handleError'](error.message, error.stack);
    }
  }
}
